import { Button } from "@/components/ui/button";
import { Agent } from "@/types/agent/agent";
import { PhoneCheckKind } from "@/types/agent/brand-integrity";
import { getPhoneCheckBlocker, PHONE_CHECK_LABELS } from "@/utils/agent/phoneCheck";

const PHONE_TESTS: Array<{ kind: PhoneCheckKind; channel: string; description: string }> = [
  {
    kind: "handoff",
    channel: "Website visitors",
    description:
      "Does your Genie offer a live call with a person when a website visitor needs one, and hold back when they don't? Uses your escalation numbers.",
  },
  {
    kind: "transfer",
    channel: "Phone callers",
    description:
      "Does your Genie put a phone caller through when they need a person, and pick the right transfer number for what they asked? Uses your transfer numbers.",
  },
];

interface PhoneTestsPanelProps {
  agent: Agent | null;
  onRunCheck: (kind: PhoneCheckKind, agent: Agent) => void;
}

export const PhoneTestsPanel = ({ agent, onRunCheck }: PhoneTestsPanelProps) => {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PHONE_TESTS.map((test) => {
          const blocker = agent ? getPhoneCheckBlocker(test.kind, agent) : null;

          return (
            <div key={test.kind} className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <div className="h-full rounded-[10px] bg-white p-5 flex flex-col">
                <p className="text-xs font-semibold uppercase tracking-wider text-genie-muted">{test.channel}</p>
                <h3 className="text-lg font-semibold text-gray-900 mt-1">{PHONE_CHECK_LABELS[test.kind]}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed flex-1">{test.description}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => agent && onRunCheck(test.kind, agent)}
                    disabled={!agent || !!blocker}
                    className="min-h-[44px] border-genie-primary text-genie-primary hover:bg-genie-primary-light font-semibold"
                  >
                    Run {PHONE_CHECK_LABELS[test.kind]}
                  </Button>
                  {blocker && <p className="text-sm text-genie-muted">{blocker}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-genie-muted mt-3">
        {agent
          ? "Each check plays five situations against the Genie. No number is dialled."
          : "Select a Genie above to run a phone test. No number is dialled."}
      </p>
    </div>
  );
};
